/**
 * Case Timeline PDF Template
 * Generates a chronological timeline of all case events
 */

import {
  createPDF,
  addHeader,
  addFootersToAllPages,
  addSectionTitle,
  addKeyValue,
  addStatusBadge,
  checkPageBreak,
  generateFilename,
  downloadPDF,
  addTableOfContents,
  drawTimelineMarker,
  anonymizeName,
} from '../pdfGenerator';
import {
  colors,
  fontSizes,
  page,
  statusColors,
  priorityColors,
  hexToRgb,
  formatDate,
  formatDateTime,
  getStatusDisplay,
  getPriorityDisplay,
  documentTypes,
  eventIcons,
} from '../pdfStyles';

/**
 * Generate Case Timeline PDF
 * @param {Object} caseData - Full case data
 * @param {Array} timelineEvents - Events from case_timeline_view
 * @param {Object} options - Export options
 * @returns {Promise<string>} - Filename
 */
export const generateCaseTimelinePDF = async (caseData, timelineEvents = [], options = {}) => {
  const { exportedBy = 'System', tenantName = 'Company', anonymize = false, returnBytes = false } = options;

  // Helper to conditionally anonymize names
  const getName = (name) => (anonymize ? anonymizeName(name) : name);

  const doc = createPDF('portrait');
  const headerOptions = {
    documentType: documentTypes.CASE_TIMELINE,
    tenantName,
  };

  let y = addHeader(doc, headerOptions);

  // Document title
  doc.setFontSize(fontSizes.title);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.text));
  doc.text('CASE TIMELINE REPORT', page.marginLeft, y + 5);
  y += 5;

  // Anonymization notice
  if (anonymize) {
    doc.setFontSize(fontSizes.small);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...hexToRgb(colors.warning));
    doc.text('(Names anonymized)', page.marginLeft, y + 5);
  }
  y += 10;

  // ===== TABLE OF CONTENTS =====
  const tocSections = [
    { title: 'Case Information', page: 1 },
    { title: 'Staff Member', page: 1 },
    { title: 'Timeline', page: 1 },
    { title: 'Summary', page: 2 },
  ];
  y = addTableOfContents(doc, tocSections, y, headerOptions);

  // Start new page after TOC
  doc.addPage();
  y = addHeader(doc, headerOptions);

  // ===== CASE INFORMATION =====
  y = addSectionTitle(doc, 'Case Information', y);

  y = addKeyValue(doc, 'Case ID', caseData.case_id, y);
  y = addKeyValue(doc, 'Title', caseData.title, y);
  y = addKeyValue(doc, 'Type', caseData.case_type, y);

  // Status with badge
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text('Status:', page.marginLeft, y);
  addStatusBadge(doc, getStatusDisplay(caseData.status), page.marginLeft + 40, y, statusColors);
  y += 5;

  // Priority with badge
  doc.setFontSize(fontSizes.body);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text('Priority:', page.marginLeft, y);
  addStatusBadge(doc, getPriorityDisplay(caseData.priority), page.marginLeft + 40, y, priorityColors);
  y += 5;

  y = addKeyValue(doc, 'Created', formatDate(caseData.actual_created_date || caseData.created_at), y);

  y += 5;

  // ===== STAFF MEMBER =====
  y = addSectionTitle(doc, 'Staff Member', y);

  const staff = caseData.staff || {};
  y = addKeyValue(doc, 'Name', getName(staff.full_name) || 'Not Assigned', y);
  y = addKeyValue(doc, 'Employee ID', staff.employee_id || 'N/A', y);
  y = addKeyValue(doc, 'Department', staff.department || 'N/A', y);

  y += 5;

  // ===== TIMELINE =====
  // Start timeline on new page
  doc.addPage();
  y = addHeader(doc, headerOptions);
  y = addSectionTitle(doc, 'Timeline', y);

  if (!timelineEvents || timelineEvents.length === 0) {
    doc.setFontSize(fontSizes.body);
    doc.setTextColor(...hexToRgb(colors.textLight));
    doc.text('No timeline events recorded.', page.marginLeft, y);
    y += 8;
  } else {
    // Sort events by date (oldest first) for chronological reading
    const sortedEvents = [...timelineEvents].sort((a, b) => {
      return new Date(a.event_date) - new Date(b.event_date);
    });

    // Group events by date
    const eventsByDate = {};
    sortedEvents.forEach((event) => {
      const dateKey = formatDate(event.event_date);
      if (!eventsByDate[dateKey]) {
        eventsByDate[dateKey] = [];
      }
      eventsByDate[dateKey].push(event);
    });

    // Render timeline
    Object.entries(eventsByDate).forEach(([dateStr, events]) => {
      // Check for page break before date header
      y = checkPageBreak(doc, y, 20, headerOptions);

      // Date header
      doc.setFontSize(fontSizes.subheading);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...hexToRgb(colors.primary));
      doc.text(dateStr, page.marginLeft, y);
      y += 6;

      // Events for this date (time is used for ordering but not displayed)
      events.forEach((event, index) => {
        y = checkPageBreak(doc, y, 15, headerOptions);

        const icon = eventIcons[event.event_type] || '';
        const isLastEvent = index === events.length - 1;

        // Draw timeline marker
        const markerX = page.marginLeft + 2;
        drawTimelineMarker(doc, markerX, y - 1, event.event_type);

        // Draw connector line to next event (if not last)
        if (!isLastEvent) {
          doc.setDrawColor(...hexToRgb(colors.border));
          doc.setLineWidth(0.3);
          doc.line(markerX, y + 1, markerX, y + 12);
        }

        // Event type indicator (if exists)
        if (icon) {
          doc.setFontSize(fontSizes.small);
          doc.setTextColor(...hexToRgb(colors.secondary));
          doc.text(icon, page.marginLeft + 8, y);
        }

        // Event title
        doc.setFontSize(fontSizes.body);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...hexToRgb(colors.text));

        let titleText = event.event_title || 'Event';
        if (event.is_legal) titleText += ' [LEGAL]';
        if (event.is_acas) titleText += ' [ACAS]';

        const titleX = icon ? page.marginLeft + 20 : page.marginLeft + 8;
        doc.text(titleText, titleX, y);
        y += 4;

        // Stage name
        if (event.stage_name) {
          doc.setFontSize(fontSizes.small);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...hexToRgb(colors.textLight));
          doc.text(`Stage: ${event.stage_name}`, page.marginLeft + 12, y);
          y += 4;
        }

        // Description
        if (event.event_description) {
          doc.setFontSize(fontSizes.small);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...hexToRgb(colors.secondary));

          const descLines = doc.splitTextToSize(event.event_description, page.contentWidth - 20);
          descLines.forEach((line) => {
            y = checkPageBreak(doc, y, 5, headerOptions);
            doc.text(line, page.marginLeft + 12, y);
            y += 3.5;
          });
        }

        y += 4;
      });

      y += 4;
    });
  }

  // ===== SUMMARY =====
  y = checkPageBreak(doc, y, 40, headerOptions);
  y = addSectionTitle(doc, 'Summary', y);

  // Calculate statistics
  const stats = {
    total: timelineEvents.length,
    actions: timelineEvents.filter((e) => e.event_type === 'action_completed').length,
    documents: timelineEvents.filter((e) => e.event_type === 'document_uploaded').length,
    notes: timelineEvents.filter((e) => e.event_type === 'note_added').length,
    stages: timelineEvents.filter((e) => e.event_type === 'stage_started' || e.event_type === 'stage_completed')
      .length,
    legal: timelineEvents.filter((e) => e.is_legal).length,
    acas: timelineEvents.filter((e) => e.is_acas).length,
  };

  y = addKeyValue(doc, 'Total Events', stats.total.toString(), y);
  y = addKeyValue(doc, 'Actions Completed', stats.actions.toString(), y);
  y = addKeyValue(doc, 'Documents Uploaded', stats.documents.toString(), y);
  y = addKeyValue(doc, 'Notes Added', stats.notes.toString(), y);
  y = addKeyValue(doc, 'Stage Changes', stats.stages.toString(), y);
  y = addKeyValue(doc, 'Legally-Critical Steps', stats.legal.toString(), y, {
    labelWidth: 55,
    valueColor: stats.legal > 0 ? colors.danger : colors.text,
  });
  y = addKeyValue(doc, 'ACAS-Compliant Steps', stats.acas.toString(), y, {
    labelWidth: 55,
    valueColor: stats.acas > 0 ? colors.warning : colors.text,
  });

  y += 5;

  // ===== EXPORT METADATA =====
  y = checkPageBreak(doc, y, 20, headerOptions);
  doc.setDrawColor(...hexToRgb(colors.border));
  doc.setLineWidth(0.5);
  doc.line(page.marginLeft, y, page.contentWidth + page.marginLeft, y);
  y += 5;

  doc.setFontSize(fontSizes.small);
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.text(`Export Type: Case Timeline (Full History)${anonymize ? ' - Anonymized' : ''}`, page.marginLeft, y);
  y += 4;
  doc.text(`Exported: ${formatDateTime(new Date())}`, page.marginLeft, y);
  y += 4;
  doc.text(`Exported By: ${exportedBy}`, page.marginLeft, y);

  // Add footers to all pages
  addFootersToAllPages(doc, {
    exportedBy,
    exportDate: new Date(),
  });

  // Generate filename
  const filename = generateFilename(caseData.case_id, 'case_timeline');

  // Return bytes for merging, or download directly
  if (returnBytes) {
    const pdfBytes = doc.output('arraybuffer');
    return { bytes: new Uint8Array(pdfBytes), filename };
  }

  downloadPDF(doc, filename);
  return filename;
};

export default generateCaseTimelinePDF;
